import { useIgnoreNotification } from '@services/banking/mutations/useIgnoreNotification'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'

import type { BankNotification } from '../types'

interface Props {
  notification: BankNotification | null
  onClose: () => void
}

export function IgnoreNotificationDialog({ notification, onClose }: Props) {
  const { t } = useTranslation('banking')
  const [reason, setReason] = useState('')
  const ignore = useIgnoreNotification()

  useEffect(() => setReason(''), [notification?.id])

  const confirm = () => {
    if (!notification || !reason.trim()) return
    ignore.mutate(
      { id: notification.id, reason: reason.trim() },
      { onSuccess: onClose }
    )
  }

  return (
    <Dialog open={!!notification} onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('ignore.title')}</DialogTitle>
        </DialogHeader>

        <div className="space-y-2">
          <Label htmlFor="ignore-reason">{t('ignore.reason')}</Label>
          <Input
            id="ignore-reason"
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            placeholder={t('ignore.reasonPlaceholder')}
          />
          <p className="text-xs text-ink/50">{t('ignore.hint')}</p>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            {t('cancel')}
          </Button>
          <Button
            onClick={confirm}
            disabled={!reason.trim() || ignore.isPending}
          >
            {t('ignore.confirm')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
